'use client';

import { cn } from '@lib/util/cn';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavCategory } from 'types/view-models';

const TREATMENTS_ID = 'treatments';

type CategoryDropdownProps = {
  categories: NavCategory[];
};

const CategoryDropdown = ({ categories }: CategoryDropdownProps) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isOpen = activeId !== null;

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setActiveId(null), 150);
  }, []);

  const handleCategoryEnter = useCallback(
    (id: string) => {
      cancelClose();
      setActiveId(id);
    },
    [cancelClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveId(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: mouse hover is progressive enhancement on top of button click/focus */}
      <div
        className="h-full flex items-center"
        onMouseEnter={() => handleCategoryEnter(TREATMENTS_ID)}
        onMouseLeave={scheduleClose}
      >
        <LocalizedClientLink
          href="/conditions"
          className={cn(
            'flex items-center h-full px-3 border-transparent hover:border-brand-interactive-500 text-base xl:text-lg font-normal text-white transition-colors duration-200 border-b-4 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset',
            activeId === TREATMENTS_ID && 'border-brand-interactive-500',
          )}
          aria-expanded={activeId === TREATMENTS_ID}
          aria-haspopup="true"
          aria-controls="category-tray"
          onFocus={() => handleCategoryEnter(TREATMENTS_ID)}
          onBlur={scheduleClose}
          onClick={() => setActiveId(null)}
        >
          Treatments &amp; Conditions
        </LocalizedClientLink>
      </div>

      {isOpen && (
        <div
          className="absolute top-full left-0 right-0 h-screen bg-black/20 z-40 pointer-events-none"
          aria-hidden="true"
        />
      )}

      {/* Always mounted so switching categories doesn't re-animate the tray */}
      <nav
        id="category-tray"
        className={cn(
          'absolute top-full left-0 right-0 bg-moss-25 border-b border-brand-neutral-200 shadow-[0_4px_12px_rgba(0,0,0,0.08)] z-[50] pb-px transition-[opacity,transform] duration-200 motion-reduce:transition-none',
          isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 -translate-y-1 pointer-events-none',
        )}
        aria-hidden={!isOpen}
        inert={!isOpen}
        aria-label="Category navigation"
        onMouseEnter={cancelClose}
        onMouseLeave={scheduleClose}
        onFocus={cancelClose}
        onBlur={scheduleClose}
      >
        <div className="content-container px-10 py-8">
          <div className="grid grid-cols-5 gap-x-10 gap-y-1">
            {categories.map((category) => (
              <LocalizedClientLink
                key={category.id}
                href={`/${category.handle}`}
                prefetch={false}
                className="text-sm text-fg-neutral-strong py-1 w-max relative before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-fg-neutral-strong before:origin-left before:scale-x-0 before:transition-transform before:duration-200 hover:before:scale-x-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-interactive-600 focus-visible:rounded-sm"
                onClick={() => setActiveId(null)}
              >
                {category.name}
              </LocalizedClientLink>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};

export default CategoryDropdown;
